import { useMemo, useState } from 'react';
import type { OccupationGroupAlignment } from '../../types/metro';
import { cn, formatNumber, shortageCell } from '../../lib/utils';
import { RankingsSeverityPill } from '../rankings/RankingsSeverityPill';
import { RowPeekDrawer } from '../shared/RowPeekDrawer';
import { sortGroups, type DetailSortKey, type DetailSortDir } from './detail-table-sort';

/**
 * Metro detail table — horizontal-scrolling table with the first
 * column (Occupational Group) frozen on the left so the group label
 * stays visible while the user scrolls through the metric columns.
 *
 * Visual style intentionally matches the Rankings table so the two
 * pages feel like the same product:
 *
 *   - White table chrome on a white card.
 *   - Header row: `border-b-2 border-navy`, `text-[13px]`,
 *     `font-semibold uppercase tracking-[0.05em]`.
 *   - Sort affordance on every column: single ▲ caret with visible resting affordance (opacity-40,
 *     bumped from opacity-0 in PR #71 follow-up). Identical
 *     glyph + animation to `RankingsTableView`'s SortableHeader.
 *
 * Columns (DEC-057 / detail-wire-real-data, relabeled 2026-06-05):
 *   - Springboard occupational group (frozen left)
 *   - Annual credentials awarded (`awardsSb`)
 *   - Annual job openings (`numOpeningsTotal`)
 *   - Credentials-to-jobs ratio (severity pill, continuous gradient)
 *   - Annual credential shortage (`shortage`)
 *
 * Removed 2026-06-05 (Maria): Status column. The ratio's continuous
 * severity pill already encodes the shortage/surplus signal in color
 * + numeric form, so the trailing categorical badge was redundant
 * and the width it claimed crowded the more informative ratio +
 * shortage columns.
 *
 * Cut placeholder columns (no source data in the xlsx):
 *   - YoY change
 *   - vs national
 *   - National rank
 *   - Providers count
 *
 * The sorter (`sortGroups`) lives in `./detail-table-sort.ts` so
 * tests can hit it without rendering the component, and so this
 * file stays component-only for the react-refresh rule.
 */

interface Column {
  readonly key: DetailSortKey;
  readonly label: string;
  readonly widthPx: number;
  readonly align?: 'left' | 'right' | 'center';
}

// Detail table column labels (Maria 2026-06-05 ask): make explicit
// that these are annual metrics (credentials awarded per year, job
// openings per year, etc.) -- the old "Springboard credentials" /
// "Job openings total" / "Shortage gap" labels under-communicated
// the time axis and read awkwardly alongside the metro headline.
// Column widths bumped to fit the longer labels without truncation.
//
// CR-02 (2026-07-07): row-click drill-down ships now, gated on the
// REAL `sbOccupationCount` field the backend populates from the
// `sb_list` tab (every (metro, group) combo has a nonzero count in
// production data). The occupation TITLES (`sbOccupations`) are
// still empty everywhere pending Georgetown's `occ_title` cleanup;
// the drill-down modal shows the count plus an honest "titles
// pending" note in that case, and switches to rendering the real
// list automatically once titles land — zero further FE changes.
//
// CR-07 (2026-07-08, Maria 5a / client G2): all columns left-align,
// header and content alike -- the ratio, credentials, jobs, and
// shortage columns were previously right/center-aligned while their
// headers matched, but the table read inconsistently against
// Rankings/Providers once the client asked for uniform left
// alignment across every table in the tool. `widthPx` below is now
// a `min-width` (CR-16), not a hard `width`, so columns can grow to
// fill the container on wide viewports -- see the `<td>`/`<th>`
// `style` usages further down.
const COLUMNS: ReadonlyArray<Column> = [
  { key: 'group', label: 'Springboard occupational group', widthPx: 200 },
  { key: 'credentials', label: 'Annual credentials awarded', widthPx: 200 },
  { key: 'jobs', label: 'Annual job openings', widthPx: 170 },
  { key: 'ratio', label: 'Credentials-to-jobs ratio', widthPx: 160 },
  { key: 'shortage', label: 'Annual credential shortage', widthPx: 200 },
];

const FROZEN_EDGE_SHADOW = 'shadow-[6px_0_8px_-4px_rgba(20,65,117,0.12)]';

interface SortHeaderProps {
  readonly column: Column;
  readonly sortKey: DetailSortKey | null;
  readonly sortDir: DetailSortDir;
  readonly onSort: (key: DetailSortKey) => void;
  readonly extraClasses?: string;
  readonly style?: React.CSSProperties;
}

/**
 * Header cell with the same sort affordance the Rankings table
 * uses (single ▲ caret, opacity 0.4 → 0.7 → 1 (IMP-004 mirror, PR #71 follow-up), rotates 180° on
 * descending). Kept inline rather than reusing `SortableHeader`
 * from `rankings/` because that component is typed against the
 * Rankings sort-key union and we don't want to widen its API.
 */
function SortHeader({ column, sortKey, sortDir, onSort, extraClasses, style }: SortHeaderProps) {
  const isActive = sortKey === column.key;
  const alignClass =
    column.align === 'right' ? 'text-right justify-end' :
    column.align === 'center' ? 'text-center justify-center' :
    'text-left justify-start';
  const ariaSort = isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      style={style}
      className={cn(
        'group h-9 cursor-pointer select-none border-b-2 border-navy bg-white px-3 text-[13px] font-semibold text-text-3 transition-colors hover:text-navy whitespace-nowrap',
        extraClasses,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className={cn(
          'inline-flex w-full items-center gap-1 transition',
          alignClass,
          isActive ? 'text-navy' : '',
        )}
      >
        <span>{column.label}</span>
        <span
          aria-hidden="true"
          className={cn(
            'inline-block text-[10px] leading-none transition-all',
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70',
            isActive && sortDir === 'desc' ? 'rotate-180' : '',
          )}
        >
          ▲
        </span>
      </button>
    </th>
  );
}

interface DetailTableProps {
  readonly groups: ReadonlyArray<OccupationGroupAlignment>;
}

interface DetailTablePropsExtended extends DetailTableProps {
  /**
   * Optional metro name — surfaces in the sb_list drill-down modal
   * title ("Springboard occupations in {group} — {metro}"). Falls
   * back to a generic title when not passed.
   */
  readonly metroName?: string;
}

export function DetailTable({ groups, metroName }: DetailTablePropsExtended) {
  const [sortKey, setSortKey] = useState<DetailSortKey | null>('ratio');
  const [sortDir, setSortDir] = useState<DetailSortDir>('asc');
  // CR-02 (2026-07-07): row-click opens a drill-down modal showing
  // the specific springboard occupations that make up the clicked
  // occupational group in this metro (Georgetown's `sb_list` tab in
  // the source spreadsheet). The gate is `sbOccupationCount` — the
  // REAL, currently-populated per-(metro, group) count — not
  // `sbOccupations.length` (the title list, still empty everywhere
  // pending Georgetown's `occ_title` cleanup). The modal itself shows
  // the count plus an honest "titles pending" note until the titles
  // land; no further frontend change is needed when they do.
  const [peekedGroup, setPeekedGroup] = useState<OccupationGroupAlignment | null>(null);

  const sortedGroups = useMemo(
    () => sortGroups(groups, sortKey, sortDir),
    [groups, sortKey, sortDir],
  );

  // CR-02 (2026-07-07, supersedes finding #3 gate): the row-click
  // drill-down affordance is gated on `sbOccupationCount` (real data,
  // populated for every group in production today), not on the
  // title list. Only rows whose group carries a nonzero count are
  // clickable; the caption only promises the feature when at least
  // one visible group has data.
  const hasAnySbData = useMemo(
    () => groups.some((group) => (group.sbOccupationCount ?? 0) > 0),
    [groups],
  );

  const handleSort = (key: DetailSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-white">
      <div className="border-b border-border px-5 py-4">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-text-4">
          Breakdown
        </span>
        <h2 className="text-base font-bold text-navy">Metro area details</h2>
        {/* CR-02 (2026-07-07): the row-click caption only advertises
            the drill-down when at least one visible group actually
            carries a nonzero `sbOccupationCount` -- otherwise it
            falls back to the scroll-hint caption so the tool never
            promises an affordance that dead-ends on every row. This
            IS the discoverability note Maria asked for (5a). */}
        <p className="mt-1 text-xs leading-[1.5] text-text-3">
          {hasAnySbData
            ? 'Click a row to see the springboard occupations in that occupational group.'
            : 'Scroll horizontally to view all metrics. The occupational group column stays pinned.'}
        </p>
      </div>
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full border-separate border-spacing-0 text-left text-sm">
          <thead className="sticky top-0 z-20">
            <tr>
              {COLUMNS.map((col, i) => {
                const isFirst = i === 0;
                const stickyClasses = isFirst
                  ? `sticky left-0 z-30 bg-white ${FROZEN_EDGE_SHADOW}`
                  : 'bg-white';
                return (
                  <SortHeader
                    key={col.key}
                    column={col}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                    extraClasses={stickyClasses}
                    // CR-16 (2026-07-08, Maria "+"): `width` dropped to
                    // `minWidth` so the browser's auto table layout can
                    // grow columns to fill the container on wide
                    // desktop viewports instead of leaving whitespace
                    // (or, on narrower viewports, overflowing before
                    // the container was actually full). `w-full` on
                    // the <table> below is what lets columns expand;
                    // `minWidth` is only the floor that keeps the
                    // longest labels from wrapping awkwardly.
                    style={{ minWidth: col.widthPx }}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedGroups.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-5 py-12 text-center text-sm text-text-3"
                >
                  No groups match the current filters.
                </td>
              </tr>
            ) : (
              sortedGroups.map((g) => {
                // CR-02 (2026-07-07): the click affordance is
                // per-row, gated on this group's REAL
                // `sbOccupationCount` (not the still-empty title
                // list). A row with a zero/undefined count stays a
                // plain inert row -- no cursor-pointer, no
                // aria-haspopup, no click/keyboard handler.
                const isClickable = (g.sbOccupationCount ?? 0) > 0;
                return (
                <tr
                  key={g.name}
                  // 2026-07-03 (V2 outstanding drill-down): row click
                  // opens the sb_list modal. IMPORTANT: we DO NOT set
                  // role="button" on the <tr> because that override
                  // wipes the default `role="row"` semantics that
                  // testing-library queries rely on (and screen
                  // readers use to announce rows/columns). The
                  // clickable/focusable affordance lives on the first
                  // cell instead — mirrors the pattern
                  // RankingsRow.tsx uses for the same reason.
                  className={cn(
                    'border-b border-border-light text-sm',
                    isClickable ? 'cursor-pointer hover:bg-ice' : '',
                  )}
                  onClick={isClickable ? () => setPeekedGroup(g) : undefined}
                >
                  <td
                    style={{ minWidth: COLUMNS[0]!.widthPx }}
                    className={cn(
                      'sticky left-0 z-10 border-b border-border/60 bg-white px-3 py-3',
                      FROZEN_EDGE_SHADOW,
                    )}
                  >
                    {/* WCAG 1.3.1 / 4.1.2 (Siteimprove/Alfa sia-r46): the
                        drill-down affordance is a real <button> INSIDE the cell,
                        not role="button" ON the <td> (which strips the cell role
                        and unassigns the column header). stopPropagation so the
                        button doesn't double-fire the row's mouse onClick. */}
                    {isClickable ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPeekedGroup(g);
                        }}
                        aria-haspopup="dialog"
                        aria-label={`Show springboard occupations in ${g.name}`}
                        className="block max-w-full text-left font-medium text-text-1"
                      >
                        {g.name}
                      </button>
                    ) : (
                      <span className="block max-w-full font-medium text-text-1">{g.name}</span>
                    )}
                  </td>
                  <td
                    style={{ minWidth: COLUMNS[1]!.widthPx }}
                    className="border-b border-border/60 bg-white px-3 py-3 text-left font-mono tabular-nums text-text-2"
                  >
                    {formatNumber(g.awardsSb)}
                  </td>
                  <td
                    style={{ minWidth: COLUMNS[2]!.widthPx }}
                    className="border-b border-border/60 bg-white px-3 py-3 text-left font-mono tabular-nums text-text-2"
                  >
                    {formatNumber(g.numOpeningsTotal)}
                  </td>
                  <td
                    style={{ minWidth: COLUMNS[3]!.widthPx }}
                    className="border-b border-border/60 bg-white px-3 py-3 text-left"
                  >
                    <RankingsSeverityPill ratio={g.ratio} />
                  </td>
                  <td
                    style={{ minWidth: COLUMNS[4]!.widthPx }}
                    className="border-b border-border/60 bg-white px-3 py-3 text-left font-mono tabular-nums text-text-2"
                  >
                    {shortageCell(g.status, g.shortage)}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* 2026-07-02 (V2 feedback): the previous line surfaced the
          `occupationGroups per metro in the /api/metros response`
          API path as if it were user-facing copy. Reviewers flagged
          it as a leaked implementation detail. Replaced with a plain
          count so the table's row total still reads at a glance. */}
      <p className="border-t border-border px-5 py-3 text-[11px] text-text-4">
        Showing {sortedGroups.length} {sortedGroups.length === 1 ? 'occupational group' : 'occupational groups'} for the selected metro.
      </p>

      {/* Drill-down modal (CR-02, 2026-07-07). Opens when the user
          clicks any occupational-group row whose `sbOccupationCount`
          is nonzero. The count headline is always real data; the
          title list below it renders from `sbOccupations` when
          Georgetown has filled `occ_title`, and shows an honest
          "titles pending" note otherwise. No code change is needed
          when the titles land — the `sbOccupations.length > 0`
          branch just starts firing. */}
      <RowPeekDrawer
        isOpen={peekedGroup !== null}
        onClose={() => setPeekedGroup(null)}
        title={
          peekedGroup && metroName
            ? `${peekedGroup.name} in ${metroName}`
            : peekedGroup?.name ?? ''
        }
        eyebrow="SPRINGBOARD OCCUPATIONS"
        variant="modal"
      >
        {peekedGroup && (
          <>
            <p className="text-sm text-text-1">
              <strong className="font-semibold text-navy">
                {peekedGroup.sbOccupationCount ?? 0}
              </strong>{' '}
              springboard {(peekedGroup.sbOccupationCount ?? 0) === 1 ? 'occupation' : 'occupations'} in{' '}
              <strong className="font-semibold text-navy">{peekedGroup.name}</strong>
              {metroName ? ` in ${metroName}` : ''}.
            </p>
            <p className="rounded-lg bg-ice/60 p-4 text-sm text-text-2">
              Springboard occupations are middle-skill occupations that offer
              workers above-median earnings at the start of their careers and
              strong wage growth over time.
              {peekedGroup.sbOccupations && peekedGroup.sbOccupations.length > 0 ? (
                <>
                  {' '}The list below shows which springboard occupations
                  Georgetown identified inside{' '}
                  <span className="font-semibold text-navy">{peekedGroup.name}</span>{' '}
                  for this metro.
                </>
              ) : null}
            </p>
            {peekedGroup.sbOccupations && peekedGroup.sbOccupations.length > 0 ? (
              <ul
                className="grid grid-cols-1 gap-1.5 sm:grid-cols-2"
                aria-label={`Springboard occupations in ${peekedGroup.name}`}
              >
                {peekedGroup.sbOccupations.map((occ) => (
                  <li
                    key={occ}
                    className="rounded-md border border-border-light bg-white px-3 py-2 text-sm text-text-1"
                  >
                    {occ}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rounded-md border border-dashed border-border bg-white p-6 text-center text-sm text-text-3">
                <p>
                  Occupation titles will appear here once Georgetown
                  finalizes the dataset.
                </p>
              </div>
            )}
          </>
        )}
      </RowPeekDrawer>
    </div>
  );
}
